type UnknownRecord = Record<string, unknown>;

function hasWindow(): boolean {
  return typeof window !== 'undefined';
}

function safeLocalStorageGet(key: string): string | null {
  if (!hasWindow()) return null;

  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function getEnvironment() {
  const hostname = window.location.hostname;

  if (hostname === 'pvolve.com' || hostname === 'www.pvolve.com') {
    return {
      apiBase: 'https://api.pvolve.com',
      appBase: 'https://app.pvolve.com',
      commerceBase: 'https://commerce-services.pvolve.com/api',
      isStaging: false,
      isProduction: true,
    }
  }

  // isStaging covers all non-production environments (local, preview, QA).
  // In the future this may be split into isDevelopment and isStaging.
  return {
    apiBase: 'https://api.qa.pvolve.com',
    appBase: 'https://app.qa.pvolve.com',
    commerceBase: 'https://commerce-services.qa.pvolve.com/api',
    isStaging: true,
    isProduction: false,
  }
}

export function isDebugEnabled(): boolean {
  if (!hasWindow()) return false;
  if ((window as unknown as { __pvDebug?: boolean }).__pvDebug) return true;

  const lsFlag = safeLocalStorageGet('pv:debug');
  if (lsFlag === '1' || lsFlag === 'true') return true;

  const params = new URLSearchParams(window.location.search);
  const qp = params.get('pvdebug');
  return qp === '1' || qp === 'true'
}

export function pvDebug(event: string, payload?: UnknownRecord): void {
  if (!isDebugEnabled()) return;

  const time = new Date().toISOString();

  if (payload) {
    console.log(`[PVDebug ${time}] ${event}`, payload);
    return;
  }

  console.log(`[PVDebug ${time}] ${event}`);
}

export function whenElementReady<T extends HTMLElement>(
  element: T,
  attribute: string,
  value: string,
): Promise<T> {
  return new Promise((resolve) => {
    if (element.dataset[attribute] === value) {
      return resolve(element);
    }

    // For camelCase cases.
    const attrName = 'data-' + attribute.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());

    const obs = new MutationObserver(() => {
      if (element.dataset[attribute] === 'true') {
        obs.disconnect();
        resolve(element);
      }
    });

    obs.observe(element, { attributes: true, attributeFilter: [attrName] });
  });
}

export function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);
}